import { sendApiRequest } from 'lib/api/public';
import type { ReactNode } from 'react';

export type HarborOnboardingVersion = 'harbor101' | 'harbor102' | 'harbor103';

export type RenderComponentProps = {
  onClose: () => void;
  onSuccess: (response?: object) => void;
  markAllTasksCompleted: () => void;
  markAsCompleted: () => void;
};

export interface OnboardingTask {
  id: string;
  title: string;
  completed: boolean;
  skip?: boolean;
  renderComponent?: (props: RenderComponentProps) => ReactNode;
}

export type OnboardingProgressResponse = {
  status: string;
  tasks: string[];
  version: HarborOnboardingVersion;
  allCompleted?: boolean;
};

export async function fetchOnboardingProgress(): Promise<OnboardingProgressResponse | null> {
  try {
    return (await sendApiRequest({
      path: '/account/onboarding-progress',
      method: 'GET',
    })) as OnboardingProgressResponse;
  } catch {
    return null;
  }
}

function versionToNumericQuery(version: HarborOnboardingVersion): string {
  return version.replace('harbor', '');
}

export async function fetchCompletedTaskIds(
  version: HarborOnboardingVersion,
  taskIds: string[],
): Promise<Set<string>> {
  const numeric = versionToNumericQuery(version);
  const results = await Promise.all(
    taskIds.map(async taskId => {
      try {
        const isDone = (await sendApiRequest({
          path: `/account/onboarding-get-task-status?version=${numeric}&taskId=${encodeURIComponent(taskId)}`,
          method: 'GET',
        })) as boolean;
        return { taskId, isDone };
      } catch {
        return { taskId, isDone: false };
      }
    }),
  );
  return new Set(results.filter(r => r.isDone).map(r => r.taskId));
}

export function mergeTasksWithCompletion(
  taskTemplates: OnboardingTask[],
  completedTaskIds: Iterable<string>,
): OnboardingTask[] {
  const done = new Set(completedTaskIds);
  return taskTemplates.map(task => ({ ...task, completed: done.has(task.id) }));
}
